// frontend/src/pages/SR_AdminReports.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { sr } from "../api/serviceReports";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// --- Email template helpers for Service Reports ---
const SR_EMAIL_LS_KEY = "jwcs.emailTemplate.serviceReport";
const SR_EMAIL_DEFAULT = `Hello,

Please find attached your service report {{number}}.

Kind regards,`;

async function loadServiceReportTemplate() {
  // Try backend first
  try {
    const res = await fetch(
      `/api/email-templates?type=${encodeURIComponent("service-report")}`
    );
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data.message === "string" && data.message.trim())
        return data.message;
    }
  } catch {
    // ignore
  }

  // Then localStorage
  try {
    const stored = window.localStorage.getItem(SR_EMAIL_LS_KEY);
    if (stored && stored.trim()) return stored;
  } catch {
    // ignore
  }

  return SR_EMAIL_DEFAULT;
}

export default function SR_AdminReports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // selection + pagination
  const [selected, setSelected] = useState(() => new Set());
  const pageSize = 20;
  const [pageIdx, setPageIdx] = useState(0);

  // email state
  const [currentReport, setCurrentReport] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // delete confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const confirmNoRef = useRef(null);

  // PDF modal
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(undefined);
  const [pdfErr, setPdfErr] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [pdfName, setPdfName] = useState("service-report.pdf");

  // responsive PDF width
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(900);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const list = await sr.reports.list();

        const normalized =
          Array.isArray(list)
            ? list
            : Array.isArray(list?.rows)
              ? list.rows
              : Array.isArray(list?.data)
                ? list.data
                : [];

        setRows(normalized);
      } catch (e) {
        setErr(String(e?.message || e));
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const rowById = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.id, r);
    return m;
  }, [rows]);

  // pagination
  const safeRows = Array.isArray(rows) ? rows : [];
  const pageCount = Math.max(1, Math.ceil(safeRows.length / pageSize));
  const boundedPageIdx = Math.min(pageIdx, pageCount - 1);
  const pageStart = boundedPageIdx * pageSize;
  const pageRows = safeRows.slice(pageStart, pageStart + pageSize);

  // selection
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedCount = selectedIds.length;

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllOnPage(checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of pageRows) checked ? next.add(r.id) : next.delete(r.id);
      return next;
    });
  }

  const allOnPageSelected =
    pageRows.length > 0 &&
    pageRows.every((r) => selected.has(r.id)) &&
    selected.size > 0;

  const someOnPageSelected =
    pageRows.some((r) => selected.has(r.id)) && !allOnPageSelected;

  // PDF helpers
  function onPdfLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  async function openPdfFor(id, displayName = "service-report.pdf") {
    setPdfErr("");
    setNumPages(null);

    if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(undefined);
    }

    setPdfName(displayName);
    setOpen(true);

    try {
      const url = sr.reports.pdfUrl(id);
      const res = await fetch(url);
      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      setPdfUrl(obj);
    } catch (e) {
      setPdfErr(String(e?.message || e));
    }
  }

  function formatDate(value) {
    if (!value) return "";
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split("-");
      return `${d}-${m}-${y}`;
    }
    const dt = new Date(s);
    if (!isNaN(dt)) {
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const yyyy = dt.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    return s;
  }

  function downloadCurrentPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = pdfName || "service-report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  useEffect(() => {
    if (!open) return;
    function updateWidth() {
      if (pdfContainerRef.current) {
        const w = pdfContainerRef.current.clientWidth;
        setPdfWidth(w > 0 ? w : 900);
      }
    }
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [open]);

  // Top actions
  const canSingleAct = selectedCount === 1;
  const canDelete = selectedCount >= 1;

  function handleTopView() {
    if (!canSingleAct) return;
    const id = selectedIds[0];
    setCurrentReport(rowById.get(id) || null);
    openPdfFor(id, `service-report-${id}.pdf`);
  }

  function handleTopPdf() {
    if (!canSingleAct) return;
    const id = selectedIds[0];
    window.open(sr.reports.pdfUrl(id), "_blank", "noopener,noreferrer");
  }

  async function emailReportById(id) {
    const row = rowById.get(id);
    if (!row) return;

    setCurrentReport(row);
    setEmailSending(true);
    setEmailMsg("");

    try {
      const template = await loadServiceReportTemplate();
      const company =
        document?.querySelector('meta[name="app-company"]')?.getAttribute(
          "content"
        ) || "Airkon";

      const msg = template
        .replace(/{{number}}/g, `#${row.id}`)
        .replace(/{{customer}}/g, row.customer_name || "")
        .replace(/{{company}}/g, company);

      const res = await fetch(`/api/service-reports/${row.id}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Service Report #${row.id}`,
          message: msg,
        }),
      });

      if (!res.ok)
        throw new Error(
          (await res.text().catch(() => "")) || `HTTP ${res.status}`
        );

      setEmailMsg(`Service report #${row.id} emailed ✓`);
      setTimeout(() => setEmailMsg(""), 4040);
    } catch (e) {
      setEmailMsg(`Failed to email: ${e.message || e}`);
    } finally {
      setEmailSending(false);
    }
  }

  function handleTopEmail() {
    if (!canSingleAct) return;
    emailReportById(selectedIds[0]);
  }

  function requestBulkDelete() {
    if (!canDelete) return;
    setDeletingIds([]);
    setConfirmOpen(true);
    setTimeout(() => confirmNoRef.current?.focus(), 0);
  }

  async function confirmBulkDelete() {
    const ids = [...selectedIds];
    const deleted = [];
    setDeletingIds([]);

    for (const id of ids) {
      try {
        // (keeping your existing endpoint path exactly as-is)
        const res = await fetch(`/api/service-reports/reports/${id}`, {
          method: "DELETE",
        });
        if (!res.ok)
          throw new Error(
            (await res.text().catch(() => "")) || `HTTP ${res.status}`
          );
        deleted.push(id);
        setDeletingIds((prev) => [...prev, id]);
        setRows((prev) => prev.filter((r) => r.id !== id));
      } catch (e) {
        alert(`Failed to delete #${id}: ${e.message || e}`);
        break;
      }
    }

    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of deleted) next.delete(id);
      return next;
    });

    setConfirmOpen(false);

    const newPageCount = Math.max(
      1,
      Math.ceil((rows.length - deleted.length) / pageSize)
    );
    if (pageIdx > newPageCount - 1) setPageIdx(newPageCount - 1);
  }

  return (
    <div className="sr-fullbleed">
      <div className="card srpage-card">
        <style>{`
          /* --- Full-bleed wrapper (MATCH CustomerForm) --- */
          .sr-fullbleed{
            min-height: 1px;
            padding: 16px clamp(12px, 2vw, 24px);
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            width: 100vw;
            max-width: 100vw;
            background: var(--card, #0a2a57);
            box-sizing: border-box;
          }

          /* Kill the full-bleed math on small screens to avoid a ~50% left gap */
          @media (max-width: 640px){
            .sr-fullbleed{
              left: auto;
              right: auto;
              margin-left: 0;
              margin-right: 0;
              width: 100%;
              max-width: 100%;
            }
          }

          /* Keep card locked to wrapper width */
          .srpage-card{
            width: 100%;
            max-width: 100%;
            min-width: 0;
            overflow: hidden;
            box-sizing: border-box;
          }

          .sr-title{
            font-weight: 700;
            margin-bottom: 12px;
          }

          .sr-topbar{
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            align-items: center;
            margin-bottom: 12px;
            min-width: 0;
          }

          /* Table wrapper to prevent horizontal overflow beyond top bar */
          .sr-table-wrap{
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            box-sizing: border-box;
          }

          .sr-table{
            width: 100%;
            border-collapse: collapse;
            min-width: 980px;
          }
          .sr-table th, .sr-table td{
            padding: 8px 10px;
            border-bottom: 1px solid var(--border);
            vertical-align: top;
          }
          .sr-table thead th{
            text-align: left;
            white-space: nowrap;
          }

          .sr-col-select{ width: 32px; min-width: 32px; max-width: 32px; text-align: center; }
          .sr-col-id{ width: 60px; min-width: 60px; max-width: 60px; text-align: right; white-space: nowrap; }
          .sr-col-date{ width: 130px; white-space: nowrap; }

          /* Pagination */
          .sr-pagination{
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
            margin-top: 10px;
            flex-wrap: wrap;
            min-width: 0;
          }

          .muted{ opacity: .8; }

          /* Mobile: STACK rows (portrait + landscape) */
          @media (max-width: 720px){
            .sr-table-wrap{ overflow-x: hidden; }
            .sr-table{ min-width: 0 !important; }
            .sr-table thead{ display: none; }

            .sr-table, .sr-table tbody, .sr-table tr, .sr-table td{
              display: block;
              width: 100%;
            }

            .sr-table tr{
              background: var(--card);
              border: 1px solid var(--border);
              border-radius: 12px;
              padding: 10px;
              margin-bottom: 12px;
            }

            .sr-table td{
              border: none;
              padding: 6px 0;
              display: flex;
              justify-content: space-between;
              gap: 12px;
              min-width: 0;
            }

            .sr-table td::before{
              content: attr(data-label);
              color: var(--muted);
              font-size: 12px;
              margin-right: 10px;
              flex: 0 0 auto;
            }

            .sr-table td.sr-col-select{
              display: flex;
              justify-content: flex-start;
            }
            .sr-table td.sr-col-select::before{ content: ""; }
          }
        `}</style>

        <div className="sr-title">Service Reports</div>

        {err && (
          <div
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #fecaca",
              background: "#fee2e2",
              color: "#991b1b",
            }}
          >
            {err}
          </div>
        )}

        {/* Top action bar */}
        <div className="sr-topbar">
          <button className="btn" onClick={handleTopView} disabled={!canSingleAct}>
            View
          </button>
          <button className="btn" onClick={handleTopPdf} disabled={!canSingleAct}>
            PDF
          </button>
          <button
            className="btn"
            onClick={handleTopEmail}
            disabled={
              !canSingleAct ||
              (emailSending && currentReport?.id === selectedIds[0])
            }
          >
            {emailSending && canSingleAct && currentReport?.id === selectedIds[0]
              ? "Sending…"
              : "Email"}
          </button>

          <button
            className="btn"
            onClick={requestBulkDelete}
            disabled={!canDelete}
            style={{
              background: "#991b1b",
              border: "1px solid #991b1b",
              color: "#ffffff",
            }}
          >
            Delete
          </button>

          <div className="muted" style={{ marginLeft: "auto" }}>
            {selectedCount > 0 ? `${selectedCount} selected` : "No reports selected"}
          </div>
        </div>

        {/* Table */}
        <div className="sr-table-wrap">
          <table className="sr-table table">
            <thead>
              <tr>
                <th className="sr-col-select">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={allOnPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someOnPageSelected;
                    }}
                    onChange={(e) => selectAllOnPage(e.target.checked)}
                  />
                </th>
                <th className="sr-col-id">ID</th>
                <th className="sr-col-customer">Customer</th>
                <th className="sr-col-address">Address</th>
                <th className="sr-col-engineer">Engineer</th>
                <th className="sr-col-date">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="muted"
                    style={{ padding: 16, textAlign: "center" }}
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="muted"
                    style={{ padding: 16, textAlign: "center" }}
                  >
                    No reports found.
                  </td>
                </tr>
              )}
              {!loading &&
                pageRows.map((r) => (
                  <tr key={r.id}>
                    <td className="sr-col-select">
                      <input
                        type="checkbox"
                        aria-label={`Select report ${r.id}`}
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                      />
                    </td>
                    <td className="sr-col-id" data-label="ID">
                      {r.id}
                    </td>
                    <td className="sr-col-customer" data-label="Customer">
                      {r.customer_name}
                    </td>
                    <td className="sr-col-address" data-label="Address">
                      {r.site_address}
                    </td>
                    <td className="sr-col-engineer" data-label="Engineer">
                      {r.engineer_name}
                    </td>
                    <td className="sr-col-date" data-label="Date">
                      {formatDate(r.report_date)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && rows.length > 0 && (
          <div className="sr-pagination">
            <div className="muted">
              Showing {pageStart + 1}-{Math.min(pageStart + pageSize, rows.length)}{" "}
              of {rows.length}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="btn secondary"
                onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
                disabled={boundedPageIdx === 0}
              >
                Prev
              </button>
              <div
                className="muted"
                style={{ fontSize: 12, whiteSpace: "nowrap" }}
              >
                Page {boundedPageIdx + 1} / {pageCount}
              </div>
              <button
                className="btn secondary"
                onClick={() => setPageIdx((i) => Math.min(pageCount - 1, i + 1))}
                disabled={boundedPageIdx >= pageCount - 1}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* PDF Modal */}
        {open && (
          <div
            onClick={() => setOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              zIndex: 1000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(1000px, 96vw)",
                maxHeight: "90vh",
                background: "var(--card)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                boxShadow: "var(--shadow)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--border)",
                  fontWeight: 600,
                }}
              >
                Service Report PDF
              </div>

              <div
                ref={pdfContainerRef}
                style={{ padding: 16, overflowY: "auto", overflowX: "hidden" }}
              >
                {pdfErr ? (
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 8,
                      border: "1px solid #fecaca",
                      background: "#fee2e2",
                      color: "#991b1b",
                    }}
                  >
                    Failed to load PDF: {pdfErr}
                  </div>
                ) : !pdfUrl ? (
                  <div className="muted" style={{ padding: 24, textAlign: "center" }}>
                    Loading PDF…
                  </div>
                ) : (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onPdfLoadSuccess}
                    onLoadError={(e) => setPdfErr(String(e?.message || e))}
                    onSourceError={(e) => setPdfErr(String(e?.message || e))}
                    loading={
                      <div className="muted" style={{ padding: 24, textAlign: "center" }}>
                        Loading PDF…
                      </div>
                    }
                  >
                    {Array.from(new Array(numPages || 0), (_, idx) => (
                      <Page key={`p_${idx + 1}`} pageNumber={idx + 1} width={pdfWidth} />
                    ))}
                  </Document>
                )}
              </div>

              <div
                style={{
                  padding: 12,
                  borderTop: "1px solid var(--border)",
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                {pdfUrl && (
                  <button className="btn" onClick={downloadCurrentPdf}>
                    Download
                  </button>
                )}
                <button className="btn secondary" onClick={() => setOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Delete Modal */}
        {confirmOpen && (
          <div
            onClick={() => deletingIds.length === 0 && setConfirmOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              zIndex: 1100,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-title"
              style={{
                width: "min(560px, 96vw)",
                background: "var(--card)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                boxShadow: "var(--shadow)",
              }}
            >
              <div
                id="confirm-title"
                style={{
                  padding: "14px 18px",
                  borderBottom: "1px solid var(--border)",
                  fontWeight: 700,
                }}
              >
                Confirm delete
              </div>
              <div style={{ padding: 18 }}>
                {deletingIds.length === 0 ? (
                  <div>
                    Delete <strong>{selectedCount}</strong> selected{" "}
                    {selectedCount === 1 ? "report" : "reports"}? This cannot be undone.
                  </div>
                ) : (
                  <div className="muted">
                    Deleting… {deletingIds.length}/{selectedCount}
                  </div>
                )}
              </div>
              <div
                style={{
                  padding: 12,
                  borderTop: "1px solid var(--border)",
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                }}
              >
                <button
                  ref={confirmNoRef}
                  className="btn secondary"
                  onClick={() => setConfirmOpen(false)}
                  disabled={deletingIds.length > 0}
                >
                  No
                </button>
                <button
                  className="btn"
                  onClick={confirmBulkDelete}
                  disabled={deletingIds.length > 0}
                  style={{
                    background: "#991b1b",
                    border: "1px solid #991b1b",
                    color: "#ffffff",
                  }}
                >
                  {deletingIds.length > 0 ? "Deleting…" : "Yes, delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {emailMsg && (
          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              color: emailMsg.startsWith("Service report") ? "#15803d" : "#b91c1c",
            }}
          >
            {emailMsg}
          </div>
        )}
      </div>
    </div>
  );
}